import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AnalyticsRouteTracker } from '@/components/AnalyticsRouteTracker';
import { ProtectedRoute } from './ProtectedRoute';
import { PlatformAdminRoute } from './PlatformAdminRoute';
import { AppShell } from '@/components/layout/AppShell';
import { LoginPage } from '@/pages/auth/LoginPage';
import { SignupPage } from '@/pages/auth/SignupPage';
import { OAuthCallbackPage } from '@/pages/auth/OAuthCallbackPage';
import { WelcomeProfilePage } from '@/pages/onboarding/WelcomeProfilePage';
import { MarketplaceAwsLandingPage } from '@/pages/marketplace-aws/MarketplaceAwsLandingPage';
import { AdminOrganizationsPage } from '@/pages/admin/AdminOrganizationsPage';
import { AdminOrganizationDetailPage } from '@/pages/admin/AdminOrganizationDetailPage';
import { AdminUsersPage } from '@/pages/admin/AdminUsersPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { IdentityProvidersPage } from '@/pages/identity-providers/IdentityProvidersPage';
import { DirectoryPage } from '@/pages/directory/DirectoryPage';
import { ConnectorsPage } from '@/pages/connectors/ConnectorsPage';
import { PipelinesPage } from '@/pages/pipelines/PipelinesPage';
import { IngestionJobsPage } from '@/pages/ingestion/IngestionJobsPage';
import { SourcesPage } from '@/pages/sources/SourcesPage';
import { DataCatalogPage } from '@/pages/data-catalog/DataCatalogPage';
import { DataCatalogDetailPage } from '@/pages/data-catalog/DataCatalogDetailPage';
import { PolicyStudioPage } from '@/pages/policy-studio/PolicyStudioPage';
import { AccessSimulatorPage } from '@/pages/access-simulator/AccessSimulatorPage';
import { AuditLogPage } from '@/pages/audit-log/AuditLogPage';
import { UsageBillingPage } from '@/pages/usage-billing/UsageBillingPage';
import { SettingsPage } from '@/pages/settings/SettingsPage';
import { SecuredRetrievalsPage } from '@/pages/secured-retrievals/SecuredRetrievalsPage';
import { AnswerSynthesisPage } from '@/pages/answer-synthesis/AnswerSynthesisPage';
import { PolicyOverheadPage } from '@/pages/benchmark/PolicyOverheadPage';
import { ApiKeysPage } from '@/pages/settings/ApiKeysPage';
import { RelationshipsPage } from '@/pages/relationships/RelationshipsPage';

export function Router() {
  return (
    <BrowserRouter>
      <AnalyticsRouteTracker />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/oauth/callback" element={<OAuthCallbackPage />} />

        {/* Authenticated but chromeless: one-time post-signup profiling */}
        <Route
          path="/welcome"
          element={
            <ProtectedRoute>
              <WelcomeProfilePage />
            </ProtectedRoute>
          }
        />

        {/* Authenticated but chromeless: AWS Marketplace subscription linking */}
        <Route
          path="/marketplace/aws"
          element={
            <ProtectedRoute>
              <MarketplaceAwsLandingPage />
            </ProtectedRoute>
          }
        />

        {/* Protected routes with AppShell layout */}
        <Route
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/identity-providers" element={<IdentityProvidersPage />} />
          <Route path="/directory" element={<DirectoryPage />} />
          <Route path="/connectors" element={<ConnectorsPage />} />
          <Route path="/pipelines" element={<PipelinesPage />} />
          <Route path="/ingestion" element={<IngestionJobsPage />} />
          <Route path="/sources" element={<SourcesPage />} />
          <Route path="/data-catalog" element={<DataCatalogPage />} />
          <Route path="/data-catalog/:id" element={<DataCatalogDetailPage />} />
          <Route path="/policy-studio" element={<PolicyStudioPage />} />
          <Route path="/access-simulator" element={<AccessSimulatorPage />} />
          <Route path="/answer-synthesis" element={<AnswerSynthesisPage />} />
          <Route path="/performance-self-test" element={<PolicyOverheadPage />} />
          <Route path="/secured-retrievals" element={<SecuredRetrievalsPage />} />
          <Route path="/audit-log" element={<AuditLogPage />} />
          <Route path="/usage-billing" element={<UsageBillingPage />} />
          <Route path="/organization" element={<Navigate to="/settings" replace />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/api-keys" element={<ApiKeysPage />} />
          <Route path="/relationships" element={<RelationshipsPage />} />

          {/* Internal platform admin (gated by is_platform_admin) */}
          <Route
            path="/admin/organizations"
            element={
              <PlatformAdminRoute>
                <AdminOrganizationsPage />
              </PlatformAdminRoute>
            }
          />
          <Route
            path="/admin/organizations/:id"
            element={
              <PlatformAdminRoute>
                <AdminOrganizationDetailPage />
              </PlatformAdminRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <PlatformAdminRoute>
                <AdminUsersPage />
              </PlatformAdminRoute>
            }
          />
        </Route>

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
